/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the AI companion panel: hero, question context, message list and composer.
 * @state-owner student UI store
 * @allowed-imports React; student store; workspace store; shared components, demo data and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace tutor thread
 * @side-effects schedules the simulated tutor reply
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useRef, useState } from 'react';
import { BoardIcon, MicIcon, SendIcon } from '../../../shared/components/VispoIcon';
import { PRACTICE_QUESTIONS, STEP_TRACK } from '../../../shared/lib/demo-data';
import type { ThreadMessage } from '../../../shared/lib/model';
import { currentTimeLabel } from '../../../shared/lib/selectors';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useStudentUiStore } from '../store/student-ui-store';

/** The context card as the companion panel renders it. */
export function QuestionContext({ onWork, label }: { onWork: boolean; label: string }) {
  const question = useStudentUiStore((state) => state.question);
  return (
    <div className="ctx">
      {onWork ? (
        <div className="q">
          {`第 ${question + 1} 題`}
          <em>{label}</em>
        </div>
      ) : (
        <div className="q">
          題 3-2<em>一元一次方程式 · 移項變號</em>
        </div>
      )}
      <div className="tr">
        {STEP_TRACK.map(([step, ok]) => (
          <div className={`s ${ok ? '' : 'b'}`} key={step}>
            <div className="l" />
            <u>{step}</u>
          </div>
        ))}
      </div>
    </div>
  );
}

function Message({ message, teacher }: { message: ThreadMessage; teacher: boolean }) {
  if (message.from === 'sys') {
    return (
      <div className="m sys">
        <div className="b">{message.text}</div>
      </div>
    );
  }
  if (message.from === 'cel') {
    return (
      <div className="cel">
        <div className="bg2">✓</div>
        <div>
          <b>{message.text}</b>
          <span>{message.subtitle}</span>
        </div>
        <em>+15 XP</em>
      </div>
    );
  }
  if (message.from === 'handoff') {
    return (
      <div className="handoff">
        <b>老師講完了，換我繼續陪你</b>
        <p>剛剛老師說的我都記下來了。要用講的還是打字？</p>
        <div className="row4">
          <button type="button">用語音問我</button>
          <button type="button">打字繼續</button>
          <button type="button">我自己先試試看</button>
        </div>
      </div>
    );
  }
  const mine = (teacher && message.from === 'tea') || (!teacher && message.from === 'stu');
  const who =
    message.from === 'ai'
      ? 'Grass Dog'
      : message.from === 'tea'
        ? '老師'
        : (message.by ?? (teacher ? '王柏睿' : '我'));
  return (
    <div className={`m ${mine ? 'me' : ''} ${message.from === 'tea' ? 'tea' : ''}`}>
      {message.from === 'ai' ? (
        <div className="ab av g">夥</div>
      ) : message.from === 'tea' ? (
        <div className="ab av e">師</div>
      ) : (
        <div className="ab av">{who[0]}</div>
      )}
      <div className="bd">
        <u>{`${who}${message.at ? ` · ${message.at}` : ''}${message.announcement ? ' · 公告' : ''}`}</u>
        <div className="b">{message.text}</div>
      </div>
    </div>
  );
}

export function TutorPanel({ slim }: { slim: boolean }) {
  const t = useTranslator();
  const selection = useWorkspaceUiStore((state) => state.selection);
  const section = useWorkspaceUiStore((state) => state.section);
  const thread = useStudentUiStore((state) => state.thread);
  const typing = useStudentUiStore((state) => state.typing);
  const question = useStudentUiStore((state) => state.question);
  const teacherHandoff = useStudentUiStore((state) => state.teacherHandoff);
  const pushMessage = useStudentUiStore((state) => state.pushMessage);
  const setTyping = useStudentUiStore((state) => state.setTyping);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const watching = section === 'act' && Boolean(selection);
  const line = teacherHandoff
    ? '王老師正在跟你說話，我先安靜一下。'
    : typing
      ? '讓我想一下⋯'
      : watching
        ? '我看著你寫，卡住就說一聲。'
        : '我在旁邊，卡住隨時叫我。';

  const send = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    pushMessage({ from: 'stu', text: trimmed, at: currentTimeLabel() });
    setTyping(true);
    window.setTimeout(() => {
      setTyping(false);
      pushMessage({
        from: 'ai',
        text: '很好，你抓到重點了。把 -3 移過去變成 +3，等號右邊就會是 9 + 3。試試看等於多少？',
        at: currentTimeLabel(),
      });
    }, 1300);
  };

  return (
    <>
      <div className="chero">
        <div className={`orb ${typing ? 'think' : ''}`} />
        <div className="ci">
          <b>{slim ? 'Lv.4' : 'Grass Dog'}</b>
          <p>{line}</p>
          {slim ? null : (
            <div className="lvl">
              <span className="bar">
                <i style={{ width: '64%' }} />
              </span>
              <em>Lv.4 · 640/1000 XP</em>
            </div>
          )}
        </div>
        {slim ? null : <span className="streak">連續 6 天</span>}
        <div className="sw">
          <button className="on" type="button">
            文字
          </button>
          <button type="button">語音</button>
        </div>
      </div>
      {watching ? (
        <div style={{ padding: `0 ${slim ? 18 : 24}px 8px` }}>
          <span className="watch">
            <i />
            {`正在陪你寫第 ${question + 1} 題`}
          </span>
        </div>
      ) : null}
      <QuestionContext label={selection ?? ''} onWork={watching} />
      {teacherHandoff ? (
        <div className="lockmsg">王老師接手中，AI 助教暫停。他講完之後我會繼續陪你。</div>
      ) : null}
      <div className="ms" id="ms" ref={listRef}>
        {thread.map((message, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: thread order is the identity
          <Message key={index} message={message} teacher={false} />
        ))}
        {typing ? (
          <div className="m">
            <div className="ab av g">夥</div>
            <div className="bd">
              <div className="b typing">
                <i />
                <i />
                <i />
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className="cmp">
        <div className="chips">
          <button type="button" onClick={() => send('我卡在第二步')}>
            我卡在第二步
          </button>
          <button type="button" onClick={() => send('給我一個提示就好')}>
            給我一個提示就好
          </button>
          {slim ? null : (
            <>
              <button type="button" onClick={() => send('用畫的給我看')}>
                用畫的給我看
              </button>
              <button type="button" onClick={() => send('換一題類似的')}>
                換一題類似的
              </button>
            </>
          )}
        </div>
        <div className="inp">
          <div className="box">
            <input
              id="ci"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                send(draft);
                setDraft('');
              }}
              placeholder={t('問夥伴任何一步')}
              value={draft}
            />
            <button className="ib" title={t('語音')} type="button">
              <MicIcon />
            </button>
            <button className="ib" title={t('白板')} type="button">
              <BoardIcon />
            </button>
          </div>
          <button
            className="sd"
            type="button"
            onClick={() => {
              send(draft);
              setDraft('');
            }}
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </>
  );
}

export const PRACTICE_TOTAL = PRACTICE_QUESTIONS.length;
